import { useQuery } from "@apollo/client";
import { ENFORCEMENT_MANUAL_VIOLATION_POLICIES, EnforcementManualViolationPoliciesData } from "common/graphql";
import { ViolationTypeOption } from "./sections/NoticeDetails/NoticeDetails";

interface UseViolationTypesResult {
  options: ViolationTypeOption[];
  loading: boolean;
  error: Error | null;
  /** Re-runs the violation-types request -- surfaced so the officer has an actual recovery
   *  step for a transient failure (e.g. a "Retry" action next to the error message), rather
   *  than the UI telling them to "try again" with no way to actually do so short of leaving
   *  and reopening the route. */
  refetch: () => void;
}

/** Fetches the officer's jurisdiction's selectable violation types (the
 *  `enforcement_manual_violation_policies` Hasura Action -- each row is already a distinct
 *  terminal enforcement.policy, not an abstracted "violation type") and maps them into the shape
 *  NoticeDetails and ViolationPenalty need. An empty array is a valid, non-error response (no
 *  violation types configured for this jurisdiction yet) -- see AUT-5574.
 *
 *  `enabled` lets the caller skip the request entirely (e.g. GenerateCitation passes
 *  `false` until it's confirmed the session is on mobile with manual citations enabled) --
 *  without it, this fires on every mount regardless of whether the officer is even eligible
 *  to see this screen, since the hook has to be called unconditionally to satisfy the rules of
 *  hooks. */
export function useViolationTypes(enabled: boolean): UseViolationTypesResult {
  const { data, loading, error, refetch } = useQuery<EnforcementManualViolationPoliciesData>(
    ENFORCEMENT_MANUAL_VIOLATION_POLICIES,
    { skip: !enabled },
  );

  // enforcement_manual_violation_policies is a Hasura Action, not a table relationship, so it
  // can't take an order_by argument the way enforcementIntentDetailFragment's own
  // child_policies(order_by: [{code: asc}]) does for the same "violation code" concept -- sorted
  // here instead, to the same order, so the dropdown doesn't reshuffle across requests.
  const options = [...(data?.enforcement_manual_violation_policies ?? [])]
    .sort((a, b) => a.code.localeCompare(b.code))
    .map((violationType) => ({
      label: `${violationType.code} — ${violationType.description}`,
      policyId: violationType.id,
      amountDueCents: violationType.amountDue,
    }));

  return {
    options,
    loading,
    error: error ?? null,
    refetch: () => {
      refetch().catch(() => undefined); // errors already surface via the `error` field above
    },
  };
}

export default useViolationTypes;
